import { DestroyRef, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';

import { Subscription } from 'rxjs';

import { BaasEditValidation } from '../baas-edit.types';
import { BaasEditFormDriver } from './baas-edit-form.driver';

/**
 * Driver for Reactive Forms (FormControl, FormGroup, FormArray).
 * For Reactive Forms, state is managed via signal getters provided by BaasEdit.
 */
export class ReactiveFormDriver implements BaasEditFormDriver {
    #initialized = false;
    #eventsSubscription: Subscription | undefined;
    #form: AbstractControl;
    #pristineInput: Signal<boolean>;
    #validInput: Signal<boolean>;
    #disabledInput: Signal<boolean>;
    #readonlyInput: Signal<boolean>;
    #validationsInput: Signal<BaasEditValidation[]>;
    #submittingInput = signal(false);
    #pendingInput = signal(false);
    #setState: (state: { valid: boolean; pristine: boolean; disabled: boolean }) => void;
    readonly pristine: Signal<boolean>;
    readonly valid: Signal<boolean>;
    readonly disabled: Signal<boolean>;
    readonly readonly: Signal<boolean>;
    readonly submitting: Signal<boolean>;
    readonly pending: Signal<boolean>;
    readonly validations: Signal<BaasEditValidation[]>;

    constructor(
        form: AbstractControl,
        pristineInput: Signal<boolean>,
        validInput: Signal<boolean>,
        disabledInput: Signal<boolean>,
        readonlyInput: Signal<boolean>,
        validationsInput: Signal<BaasEditValidation[]>,
        setState: (state: { valid: boolean; pristine: boolean; disabled: boolean }) => void
    ) {
        this.#form = form;
        this.#pristineInput = pristineInput;
        this.#validInput = validInput;
        this.#disabledInput = disabledInput;
        this.#readonlyInput = readonlyInput;
        this.#validationsInput = validationsInput;
        this.#setState = setState;
        this.pristine = this.#pristineInput;
        this.valid = this.#validInput;
        this.disabled = this.#disabledInput;
        this.readonly = this.#readonlyInput;
        this.submitting = this.#submittingInput;
        this.pending = this.#pendingInput;
        this.validations = this.#validationsInput;
    }

    onInit(destroyRef: DestroyRef): void {
        if (this.#initialized) {
            return;
        }
        this.#initialized = true;

        const assignFormSignals = () => {
            this.#setState({
                valid: this.#form.valid,
                pristine: this.#form.pristine,
                disabled: this.#form.disabled
            });
        };

        assignFormSignals();
        this.#eventsSubscription = this.#form.events.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => assignFormSignals());
    }

    dispose(): void {
        this.#eventsSubscription?.unsubscribe();
        this.#eventsSubscription = undefined;
        this.#initialized = false;
    }

    syncDisabled(disabled: boolean | undefined): void {
        if (disabled === undefined || this.#form.disabled === disabled) {
            return;
        }

        if (disabled) this.#form.disable();
        else this.#form.enable();
    }

    markAsPristine(): void {
        this.#form.markAsPristine();
        this.#form.markAsUntouched();
    }
}
